// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, IconButton, Paper, CircularProgress, Button, Link, InputAdornment } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import styles from './ChatbotUI/ChatbotUI_styles';
import { formatTime } from './ChatbotUI/utils';
import { apiURL } from '../config';
import DOMPurify from 'dompurify';
import { ReactComponent as BotIcon } from './ChatbotUI/ic_ai.svg';
import { ReactComponent as DisLikeIcon } from './ChatbotUI/ic_dislike.svg';
import { ReactComponent as LikeIcon } from './ChatbotUI/ic_like.svg';

const ChatbotUI = ({ userId }) => {
    const [chatId, setChatId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isChatSending, setChatSending] = useState(false);
    const [chatSettings, setChatSettings] = useState(null);
    const messagesEndRef = useRef(null);
    const initializationRef = useRef(false);
    const [showResetButton, setShowResetButton] = useState(false);
    const [inputError, setInputError] = useState('');
    const [displayedQuestions, setDisplayedQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [faqs, setFaqs] = useState([]);
    const [showMoreQuestionsButton, setShowMoreQuestionsButton] = useState(true);

    // 檢查 user 輸入
    const validateAndSanitizeInput = (input) => {
        const trimmedInput = input.trim();
        if (trimmedInput.length === 0) {
            return null;
        }
        if (trimmedInput.length > 500) {
            setInputError('消息不能超過 500 個字符');
            return null;
        }
        const sanitizedInput = DOMPurify.sanitize(trimmedInput);
        const dangerousPatterns = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
        if (dangerousPatterns.test(sanitizedInput)) {
            console.warn('檢測到潛在的不安全內容');
            return null;
        }
        setInputError('');
        return sanitizedInput;
    };

    // 初始化聊天
    useEffect(() => {
        if (!initializationRef.current) {
            initializeChat();
            fetchFaqs();
            initializationRef.current = true;
        }
    }, []);

    // 訊息更新時自動滾動到最新訊息
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        if (faqs.length > 0) {
            //console.log('FAQs updated, current faqs:', faqs);
            updateDisplayedQuestions(faqs.slice(0, 3));
        }
    }, [faqs]);

    // 獲取 FAQ 列表的函數
    const fetchFaqs = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${apiURL}api/faq-lists?limit=10`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (!response.ok) throw new Error('獲取FAQ列表失敗');
            const data = await response.json();
            setFaqs(data);

            // 顯示所有獲取的問題（最多10個）
            updateDisplayedQuestions(data);

            //console.log('Fetched FAQs:', data);
            //console.log('Number of FAQs:', data.length);
        } catch (error) {
            console.error('Error fetching FAQs:', error);
            setSnackbar({ open: true, message: '獲取FAQ列表失敗', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // 更新顯示的問題
    const updateDisplayedQuestions = (questions) => {
        const formattedQuestions = questions.map(faq => ({
            question: faq.question,
            url: faq.source_url
        }));
        setDisplayedQuestions(formattedQuestions);
        const shouldShowMoreButton = formattedQuestions.length < faqs.length;
        //console.log('Should show more button:', shouldShowMoreButton);
        setShowMoreQuestionsButton(shouldShowMoreButton);
    };

    // 處理"更多問題"按鈕點擊
    const handleShowMoreQuestions = () => {
        const currentCount = displayedQuestions.length;
        const newQuestions = faqs.slice(0, Math.min(currentCount + 3, faqs.length));
        //console.log('Showing more questions. New count:', newQuestions.length);
        updateDisplayedQuestions(newQuestions);
    };

    const initializeChat = async () => {
        try {
            setIsLoading(true);
            const requestBody = {
                chat_settings_id: "1",
                status: "active",
            };
            const response = await fetch(`${apiURL}api/chats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(requestBody),
            });
            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, body: ${errorBody}`);
            }
            const data = await response.json();
            setChatId(data.chat_id);
            setChatSettings({
                chat_settings_id: data.chat_settings_id,
            });
            setMessages([{
                id: 'welcome',
                text: '您好，很高興為您服務！我是 Ciao，也可以叫我「巧」 ～ 您可以問我 MailCloud 信箱的使用問題，我會提供參考文章解決您的疑惑！',
                isBot: true,
                isWelcome: true,
                timestamp: new Date(),
                hasIcon: true 
            }]);
        } catch (error) {
            console.error('Failed to initialize chat:', error);
            setSnackbar({ open: true, message: '初始對話框失敗', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const generateUniqueId = () => {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    const handleSend = async (messageText = null) => {
        messageText = messageText || input;
        const validatedInput = validateAndSanitizeInput(messageText);
        if (!validatedInput) {
            return;
        }

        if (chatId) {
            if(isChatSending){
                return;
            }
            setChatSending(true);
            const userMessage = { id: generateUniqueId(), text: validatedInput, isBot: false, timestamp: new Date() };
            setMessages(prev => [...prev, userMessage]);
            setInput('');

            //等待一秒才出現 bot 的訊息泡泡
            await new Promise(resolve => setTimeout(resolve, 1000));

            const loadingMessage = { id: generateUniqueId(), isBot: true, isLoading: true, timestamp: new Date() };
            setMessages(prev => [...prev, loadingMessage]);
            try {
                const response = await fetch(`${apiURL}api/chats/${chatId}/messages`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    },
                    body: JSON.stringify({
                        role: 'user',
                        role_content: validatedInput
                    }),
                });

                if (!response.ok) {
                    setChatSending(false);
                    throw new Error('Network response was not ok');
                }

                const store_response = await response.json();
                let formattedContent = [];
                console.log("response:",store_response)
                if (store_response.collected_data && Array.isArray(store_response.collected_data)) {
                    formattedContent = store_response.collected_data.map(item => ({
                        input: item.input,
                        output: item.output,
                        url: item.zendesk_article_html_url
                    }));
                }

                const botReply = {
                    id: generateUniqueId(),
                    text: formattedContent.length > 0 ? formattedContent : "No data available",
                    isBot: true,
                    timestamp: new Date(),
                    feedback: null,
                    questionId: store_response.message_id
                };

                setMessages(prev => prev.map(msg =>
                    (msg.isLoading && msg.isBot) ? botReply : msg
                ));
                setShowResetButton(true);

            } catch (error) {
                console.error('Failed to send message:', error);
                const errorMessage = {
                    id: generateUniqueId(),
                    text: "連線已過期，請重新整理頁面",
                    isBot: true,
                    timestamp: new Date(),
                    feedback: null
                };
                setInputError('連線已過期，請重新整理頁面')
                setMessages(prev => prev.map(msg =>
                    (msg.isLoading && msg.isBot) ? errorMessage : msg
                ));
            } finally{
                setChatSending(false);
            }
        }
    };

    const handleResetConversation = () => {
        initializeChat();
        fetchFaqs();
        setShowResetButton(false);
        setChatSending(false);
    };

    const handleFeedback = async (questionId, feedback) => {
        try {
            const currentFeedback = messages.find(msg => msg.questionId === questionId)?.feedback;
            const newFeedback = currentFeedback === feedback ? null : feedback === 'like' ? 'good' : 'poor';

            const response = await fetch(`${apiURL}api/chat-messages/${questionId}/rating`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({
                    content_rating: newFeedback
                }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const result = await response.json();
            setMessages(prevMessages =>
                prevMessages.map(msg =>
                    msg.questionId === questionId ? { ...msg, feedback: result.content_rating } : msg
                )
            );
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            setSnackbar({ open: true, message: '送出回饋失敗', severity: 'error' });
        }
    };

    const handleRecommendedQuestionClick = (item) => {
        if (item.url) {
            window.open(item.url, '_blank', 'noopener,noreferrer');
        }
    };

    const CircularBotIcon = ({ color = '#2382FE', borderColor = '#DDDDDD', style = {} }) => (
        <svg
            width="36"
            height="36"
            viewBox="0 0 36 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                display: 'block',
                flexShrink: 0,
                ...style,
            }}
        >
            <circle
                cx="18"
                cy="18"
                r="17.5"
                fill="white"
                stroke={borderColor}
                strokeWidth="1"
            />
            <g transform="translate(6, 6)">
                <BotIcon width="24" height="24" fill={color} />
            </g>
        </svg>
    );

    const TypingEffect = () => (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '24px',
            '& > span': {
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                display: 'inline-block',
                margin: '1px',
                animation: 'typingAnimation 1.4s infinite ease-in-out both',
            },
            '& > span:nth-of-type(1)': {
                backgroundColor: '#D3D3D3',
                animationDelay: '0s'
            },
            '& > span:nth-of-type(2)': {
                backgroundColor: '#E0E0E0',
                animationDelay: '0.2s'
            },
            '& > span:nth-of-type(3)': {
                backgroundColor: '#D3D3D3',
                animationDelay: '0.4s'
            },
            '@keyframes typingAnimation': {
                '0%': { transform: 'scale(0)' },
                '40%': { transform: 'scale(1)' },
                '80%': { transform: 'scale(0)' },
                '100%': { transform: 'scale(0)' },
            },
        }}>
            <span></span>
            <span></span>
            <span></span>
        </Box>
    );

    const renderMessage = (message) => (
        <Box key={message.id} sx={styles.message(message.isBot)}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: message.isBot ? 'row' : 'row-reverse' }}>
                {message.isBot && (
                    <CircularBotIcon
                        style={{
                            margin: 0,
                            verticalAlign: 'middle',
                            width: '36px',
                            height: '36px',
                            flexShrink: 0,
                        }}
                        color="#2382FE"
                    />
                )}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: message.isBot ? 'flex-start' : 'flex-end',
                    ml: message.isBot ? '8px' : 0,
                    mr: message.isBot ? 0 : '0px',
                }}>
                    <Paper elevation={0} sx={styles.messageContent(message.isBot)}>
                        {message.isLoading ? (
                            <TypingEffect />
                        ) : Array.isArray(message.text) ? (
                            message.text.map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{ marginBottom: index === message.text.length - 1 ? '0px' : '12px' }}
                                >
                                    <Link href={item.url} target="_blank" rel="noopener noreferrer">
                                        <Typography
                                            variant="body1"
                                            sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: '#2382FE' }}
                                        >
                                            {item.input}
                                        </Typography>
                                    </Link>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: '#333333' }}
                                    >
                                        {item.output}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2">
                                {message.hasIcon ? (
                                    <>
                                        Hi, 我是 Ciao (巧)，您可以問我信箱使用問題，為確保資訊精準，我僅提供文章連結，暫不支援對話哦。
                                        對了，信箱最新推出的各項 AI Ciao 巧功能 <BotIcon style={{ width: '16px', height: '16px', verticalAlign: 'middle', margin: '0 2px' }} /> 
                                        已經上線，歡迎您多加體驗！
                                    </>
                                ) : (
                                    message.text
                                )}
                            </Typography>
                        )}
                    </Paper>
                    {message.isWelcome && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            marginTop: '12px'
                        }}>
                            {displayedQuestions.map((item, qIndex) => (
                                <Button
                                    key={qIndex}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        ...styles.recommendedQuestion,
                                        marginTop: qIndex === 0 ? 0 : '12px',
                                        marginLeft: 0,
                                        marginRight: 0,
                                        marginBottom: 0,
                                        fontFamily: 'Noto Sans',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        textAlign: 'left',
                                        color: '#2382FE',
                                        textTransform: 'none',
                                        justifyContent: 'flex-start',
                                        padding: '8px 8px',
                                    }}
                                    onClick={() => handleRecommendedQuestionClick(item)}
                                >
                                    {item.question}
                                </Button>
                            ))}
                            {showMoreQuestionsButton && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        ...styles.recommendedQuestion,
                                        marginTop: '12px',
                                        marginLeft: 0,
                                        marginRight: 0,
                                        marginBottom: 0,
                                        fontFamily: 'Noto Sans',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        textAlign: 'left',
                                        color: '#2382FE',
                                        textTransform: 'none',
                                        justifyContent: 'flex-start',
                                        padding: '8px 8px',
                                    }}                                
                                    onClick={handleShowMoreQuestions}
                                >
                                    顯示更多常見問題 ...
                                </Button>
                            )}
                            {
                                //console.log('Rendering more questions button:', showMoreQuestionsButton)
                            }
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: message.isBot ? 'flex-start' : 'flex-end', alignItems: 'center', mt: 0.5, m: 0 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                mx: 1,
                                my: 1,
                                fontFamily: 'Noto Sans',
                                fontSize: '12px',
                                fontWeight: 400,
                                lineHeight: '16px',
                                textAlign: 'center',
                                color: '#999999'
                            }}
                        >
                            {formatTime(message.timestamp)}
                        </Typography>
                        {message.isBot && message.questionId && !message.isLoading && (
                            <Box sx={styles.feedbackButtons}>
                                <IconButton
                                    onClick={() => handleFeedback(message.questionId, 'like')}
                                    size="small"
                                    sx={{
                                        padding: '6px',
                                        margin: '4px',
                                        color: message.feedback === 'good' ? '#42A5F5' : '#999999'
                                    }}
                                >
                                    <LikeIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => handleFeedback(message.questionId, 'dislike')}
                                    size="small"
                                    sx={{
                                        padding: '6px',
                                        margin: '4px',
                                        color: message.feedback === 'poor' ? '#42A5F5' : '#999999'
                                    }}
                                >
                                    <DisLikeIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={styles.mainContainer}>
            <Paper elevation={3} sx={styles.chatContainer}>
                <Box
                  sx={styles.messageArea}
                >
                    {messages.map(renderMessage)}
                    <div ref={messagesEndRef} />
                </Box>
        
                {/* 重置對話按鈕 */}
                {showResetButton && (
                    <Box sx={styles.resetButton}>
                    
                        <Typography 
                        onClick={handleResetConversation} 
                        sx={styles.resetText}
                        >
                            重新開始對話
                        </Typography>
                    </Box>
                )}

                <Box sx={styles.inputArea}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="請輸入您的問題，我將為您提供最相關的參考文章"
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            if (inputError) setInputError('');
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                        disabled={isLoading}
                        error={!!inputError}
                        helperText={inputError}
                        sx={styles.inputField}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => handleSend()}
                                        disabled={isLoading}
                                        sx={{
                                            ...styles.sendButton,
                                            marginRight: '10px',
                                            color: input.trim() ? '#2382FE' : '#CCCCCC',
                                        }}
                                    >
                                        <SendIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    );
};

export default ChatbotUI;
